import { EntityStatus } from '@myre/common-core';
import {
  type ActionButtonType,
  type Button,
} from '@myre/ui-primitive-components';

import { isDevMode, TemplateRef } from '@angular/core';
import {
  AbstractControl,
  type FormGroup,
  type UntypedFormArray,
} from '@angular/forms';
import { castArray, compact, isNil } from 'lodash-es';
import { of, type Observable } from 'rxjs';

import { type ElementBase } from '../elements/element-base';
import { getElement } from '../elements/element.helper';

import { type QuestionAlternativeInput } from './question-alternative-input';
import { QuestionBase, type IQuestionBase } from './question-base';
import { QuestionCollapseIcon } from './question-collapse-icon';
import { QuestionCollapseKpi } from './question-collapse-kpi';
import { QuestionControl } from './question-control';
import { QuestionFinancialValue } from './question-financial-value';
import { QuestionHidden } from './question-hidden';
import { QuestionTitle } from './question-title';

export type CollapseToggleStrategy = 'none' | 'all' | 'onlyNew' | 'onlyOld';

type TotalValues = Record<
  string,
  { value: string | number; tooltipMessage?: string }
>;

export interface IQuestionGroup<T> extends IQuestionBase<T[]> {
  withAutoEntityStatus?: boolean;
  display?: 'list' | 'group';
  overflowX?: boolean;
  stickyLeftPanel?: boolean;
  fixedHeaderFooter?: boolean;
  footerButtons?: Button | Button[];
  displayTotalElements?: boolean;
  generateElements: (values: T) => ElementBase[];
  initialItemValue?: T;
  initialItemValueFactory?: (questionGroup: QuestionGroup<T>) => T;
  labelButton?: string;
  labelButtonType?: ActionButtonType;
  maxItems?: number;
  /**
   * For display 'list' only: when set and the list has no row, the footer shows a
   * full-width dashed button with this label (and the column headers are hidden).
   */
  emptyLabelButton?: string;
  /**
   * For display 'list' only: always-visible footer text (e.g. a counter),
   * independent of the validation state. Receives the current row count.
   */
  listFooterText?: (rowCount: number) => string;
  /**
   * For display 'list' only: render each cell's blocking error messages under the field.
   */
  showListCellErrors?: boolean;
  /**
   * For display 'list' only: drop the horizontal spacing added by `spacedList`
   * (outer row margin + footer padding) so the content aligns with sibling fields.
   */
  flushHorizontalSpacing?: boolean;
  /**
   * For display 'list' only: separate the rows, the cells of a row and the
   * footer button, instead of stacking them flush against each other.
   */
  spacedListItems?: boolean;
  showIndex?: boolean;
  canDelete?: () => boolean | Promise<boolean>;
  canDeleteItem?: (questionGroup: QuestionGroup<T>, index?: number) => boolean;
  deleteLabelButton?: string;
  groupContentPadding?: 'none' | 'small' | 'large';
  groupTitleSize?: number;
  headerTemplate?: TemplateRef<any>;
  footerTemplate?: TemplateRef<any>;
  handleEvent?: (index: number, event: any) => void;
  elementsContainerPadding?: boolean;
  notBorderedList?: boolean;
  notPaddedList?: boolean;
  notSpaced?: boolean;
  addAction?: () => any;
  deleteAction?: () => any;
  collapsable?: boolean;
  collapseToggle?: CollapseToggleStrategy;
  allowToggleSwitch?: boolean;
  deleteButtonType?: ActionButtonType;
  hasCustomActionButton?: boolean;
  customActionCallback?: (item: T, index: number) => void;
  customActionType?: ActionButtonType;
  customActionLabel?: string;
  customActionIcon?: string;
  customActionAddItem?: boolean;
  customActionLastItemOnly?: boolean;
  spacedList?: 'none' | 'small' | 'large';
  rememberCollapsable?: boolean;
  groupItemBorder?: boolean;
  elementBorder?: boolean;
  /**
   * For display 'list' only
   * Allows user to align column on left or right
   * Each given item represents a column
   */
  columnsAlign?: ('left' | 'right')[];
  showItem$?: (
    questionGroup: QuestionGroup<T>,
    index?: number,
  ) => Observable<boolean>;
  totalValuesCallback?: () => TotalValues;
  value$?: never;
}

export class QuestionGroup<T>
  extends QuestionBase<T[]>
  implements IQuestionGroup<T>
{
  /**
   * If withAutoEntityStatus is true, it automatically handles the entityStatus of the elements depending on the given initial value.
   */
  public withAutoEntityStatus: boolean;
  public display: 'list' | 'group';
  // Only for display 'list' : if true, the list will have a horizontal scroll and input size will be in pixels
  public overflowX: boolean;
  // Only for display 'list' : if true, the list will have a sticky left panel with the index and the custom action button
  public stickyLeftPanel: boolean;
  // Only for display 'list' : if true, the list will have a fixed header and footer
  public fixedHeaderFooter: boolean;
  // Only for display 'list'
  public displayTotalElements: boolean;
  // Only for display 'list'
  public footerButtons: Button[];
  public generateElements: (values: T) => ElementBase[];
  public showIndex: boolean;
  public elements: ElementBase[][] = [];
  public initialItemValue?: T;
  public initialItemValueFactory?: (questionGroup: QuestionGroup<T>) => T;
  public labelButton?: string;
  public labelButtonType?: ActionButtonType;
  public maxItems?: number;
  public emptyLabelButton?: string;
  public listFooterText?: (rowCount: number) => string;
  public showListCellErrors?: boolean;
  public flushHorizontalSpacing?: boolean;

  public spacedListItems?: boolean;
  public canDelete?: () => boolean | Promise<boolean>;
  public canDeleteItem?: (
    questionGroup: QuestionGroup<T>,
    index?: number,
  ) => boolean;

  public groupContentPadding?: 'none' | 'small' | 'large';
  public groupTitleSize?: number;
  public headerTemplate?: TemplateRef<any>;
  public footerTemplate?: TemplateRef<any>;
  public handleEvent?: (index: number, event: any) => void;
  public elementsContainerPadding?: boolean;
  public notBorderedList?: boolean;
  public notPaddedList?: boolean;
  public notSpaced?: boolean;
  public addAction?: () => any;
  public deleteAction?: () => any;
  public deleteLabelButton?: string;
  public collapsable?: boolean;
  public collapseToggle?: CollapseToggleStrategy;
  public allowToggleSwitch?: boolean;
  public deleteButtonType?: ActionButtonType;
  public hasCustomActionButton?: boolean;
  public customActionCallback?: (item: T, index: number) => void;
  public customActionType?: ActionButtonType;
  public customActionLabel?: string;
  public customActionIcon?: string;
  public customActionAddItem?: boolean;
  public customActionLastItemOnly?: boolean;
  public spacedList?: 'none' | 'small' | 'large';
  public rememberCollapsable?: boolean;
  public groupItemBorder?: boolean;
  public elementBorder?: boolean;
  public columnsAlign?: ('left' | 'right')[];
  public showItem$?: (
    questionGroup: QuestionGroup<T>,
    index?: number,
  ) => Observable<boolean>;

  // Only for display 'group': if true, a new disabled top line will be added, filled with the values passed as parameters
  // the key of the object is the key of the question
  public totalValuesCallback: () => TotalValues;

  declare public value$?: never;

  declare protected _formControl: UntypedFormArray;
  protected _controls: AbstractControl[];
  protected _collapsableToggleValues: boolean[] = [];

  private _deletedItems: ElementBase[][] = [];

  constructor(options: IQuestionGroup<T>) {
    super(options);
    this.elementType = 'group';
    this.withAutoEntityStatus = options.withAutoEntityStatus || false;
    this.display = options.display || 'group';
    this.overflowX = options.overflowX || false;
    this.stickyLeftPanel = options.stickyLeftPanel || false;
    this.footerButtons = compact(castArray(options.footerButtons));
    this.fixedHeaderFooter = options.fixedHeaderFooter || false;
    this.displayTotalElements = options.displayTotalElements || false;
    this.generateElements = options.generateElements;
    this.elementsContainerPadding = !!options.elementsContainerPadding;
    this.notSpaced = !!options.notSpaced;
    this.notBorderedList = !!options.notBorderedList;
    this.notPaddedList = !!options.notPaddedList;
    this.showIndex =
      this.display === 'group'
        ? !!options.showIndex && !this.notSpaced
        : !!options.showIndex;
    this.initialItemValue = options.initialItemValue;
    this.initialItemValueFactory = options.initialItemValueFactory;
    this.labelButton = options.labelButton;
    this.labelButtonType = options.labelButtonType;
    this.maxItems = options.maxItems;
    this.emptyLabelButton = options.emptyLabelButton;
    this.listFooterText = options.listFooterText;
    this.showListCellErrors = !!options.showListCellErrors;
    this.flushHorizontalSpacing = !!options.flushHorizontalSpacing;
    this.spacedListItems = !!options.spacedListItems;
    this.canDelete = options.canDelete;
    this.canDeleteItem = options.canDeleteItem;
    this.handleEvent = options.handleEvent;
    this.groupContentPadding = options.groupContentPadding || 'large';
    this.groupTitleSize = options.groupTitleSize;
    this.headerTemplate = options.headerTemplate;
    this.footerTemplate = options.footerTemplate;
    this.addAction = options.addAction;
    this.deleteAction = options.deleteAction;
    this.deleteLabelButton = options.deleteLabelButton;
    this.collapsable = !!options.collapsable;
    this.collapseToggle = options.collapseToggle || 'onlyNew';
    this.allowToggleSwitch = isNil(options.allowToggleSwitch)
      ? true
      : !!options.allowToggleSwitch;
    this.deleteButtonType = options.deleteButtonType || 'invisible';
    this.hasCustomActionButton = !!options.hasCustomActionButton;
    this.customActionCallback = options.customActionCallback;
    this.customActionType = options.customActionType;
    this.customActionLabel = options.customActionLabel;
    this.customActionIcon = options.customActionIcon;
    this.customActionAddItem = !!options.customActionAddItem;
    this.customActionLastItemOnly = !!options.customActionLastItemOnly;
    this.spacedList = options.spacedList || 'none';
    this.rememberCollapsable = !!options.rememberCollapsable;
    this.groupItemBorder = options.groupItemBorder;
    this.elementBorder = options.elementBorder;
    this.columnsAlign = options.columnsAlign || [];
    this.showItem$ = options.showItem$ || (() => of(true));
    this.totalValuesCallback = options.totalValuesCallback || (() => null);

    if (this.collapsable) {
      this._collapsableToggleValues = Array(
        this.initialValue?.length || 0,
      ).fill(['all', 'onlyOld'].includes(this.collapseToggle));
    }

    if (!this.defaultValue) {
      this.defaultValue = [];
    }
  }

  public getCollapsableToggleValue(index: number): boolean {
    return this._collapsableToggleValues[index];
  }

  public override initStart(): void {
    if (this.rememberCollapsable) {
      let toggleCachedValue =
        this.questionControlService.getCachedCollapseStatus(this.key);

      if (toggleCachedValue !== null) {
        toggleCachedValue = Array.isArray(toggleCachedValue)
          ? toggleCachedValue
          : [];
        toggleCachedValue.splice(this.initialValue?.length ?? 0);

        this._collapsableToggleValues = toggleCachedValue;
      }
    }
  }

  public addItem(values: T = this.initialItemValue): void {
    const updateValueAndValidityBack = this._formControl.updateValueAndValidity;
    this._formControl.updateValueAndValidity = () => {};

    const groupElements = this.generateElements(values);

    if (!groupElements) {
      return;
    }

    this.elements.push(groupElements);

    const form = this.questionControlService.generateForm(groupElements);

    this._formControl.push(form);

    for (const elt of groupElements) {
      elt.getIndex = () => this._formControl.controls.indexOf(form);
    }

    this._formControl.updateValueAndValidity = updateValueAndValidityBack.bind(
      this._formControl,
    );

    if (this.enabled) {
      this._formControl.updateValueAndValidity();
    }
  }

  public onToggleChange(on: boolean, index: number): void {
    if (typeof this.handleEvent === 'function') {
      this.handleEvent(0, { on, name: 'toggle' });
    }

    this._collapsableToggleValues[index] = on;

    if (this.rememberCollapsable) {
      this._setCachedCollapseStatus();
    }
  }

  public addNewItem(): void {
    if (this.collapsable) {
      this._collapsableToggleValues.push(
        ['all', 'onlyNew'].includes(this.collapseToggle),
      );

      if (this.rememberCollapsable) {
        this._setCachedCollapseStatus();
      }
    }

    if (this.initialItemValueFactory) {
      this.addItem(this.initialItemValueFactory(this));
    } else {
      this.addItem(this.initialItemValue);
    }

    if (this.addAction) {
      this.addAction();
    }
  }

  public removeItem(index: number | AbstractControl): void {
    const updateValueAndValidityBack = this._formControl.updateValueAndValidity;
    this._formControl.updateValueAndValidity = () => {};

    if (index instanceof AbstractControl) {
      index = this._formControl.controls.indexOf(index);
    }

    const groupItemElements = this.elements[index];

    const hasId = !!getElement<QuestionHidden<number>>(
      'id',
      groupItemElements,
    )?.getValue();

    if (hasId) {
      this._deletedItems.push(this.elements[index]);
    }

    this.elements.splice(index, 1);
    this.questionControlService.destroy(this._formControl.at(index));
    this._formControl.removeAt(index);

    if (this.collapsable) {
      this._collapsableToggleValues.splice(index, 1);

      if (this.rememberCollapsable) {
        this._setCachedCollapseStatus();
      }
    }

    this._formControl.updateValueAndValidity = updateValueAndValidityBack.bind(
      this._formControl,
    );

    if (this.enabled) {
      this._formControl.updateValueAndValidity();
    }

    if (this.deleteAction) {
      this.deleteAction();
    }
  }

  public override setValue(values: T[]): void {
    // we do not want updateValueAndValidity and _onCollectionChange to be called
    // it would trigger question changes possibly multiple times
    const updateValueAndValidityBack = this._formControl.updateValueAndValidity;
    this._formControl.updateValueAndValidity = () => {};

    // @ts-expect-error
    const _onCollectionChangeBack = this._formControl['_onCollectionChange'];
    // @ts-expect-error
    this._formControl['_onCollectionChange'] = () => {};

    // this.elements.length might change during iteration
    // it's important that we iterate from 0 to max on initial value of this.elements, no matter what's added or removed
    // (iterate from max to 0 would break logic if values.length > this.elements.length)
    const max = Math.max(values.length, this.elements.length);
    const initialElements = [...this.elements];

    for (let i = 0; i < max; i++) {
      const value = values[i];
      // we dont trust user input => values[i] can be falsely
      // i < values.length means we still are in values length range
      const hasValue = i < values.length;
      const elements = initialElements[i];

      if (elements && hasValue) {
        for (const element of elements) {
          if (element instanceof QuestionBase) {
            // @ts-expect-error
            element.setValue(value && value[element.key]);
          }
        }
      } else if (elements && !hasValue) {
        // elements exists but not value, so values.length < elements.length
        // remove remaining items
        // we might have already removed some items so make sure i is not out ouf bounds
        this.removeItem(Math.min(i, this.elements.length - 1));
      } else {
        // elements does not exist, value does
        // add new item
        this.addItem(value);
      }
    }

    // now that formControl is clear, we can reset updateValueAndValidity and _onCollectionChange
    // calling them if necessary
    this._formControl.updateValueAndValidity = updateValueAndValidityBack.bind(
      this._formControl,
    );

    // @ts-expect-error
    this._formControl['_onCollectionChange'] = _onCollectionChangeBack.bind(
      this._formControl,
    );

    if (this.enabled) {
      this._formControl.updateValueAndValidity();
      // @ts-expect-error
      this._formControl['_onCollectionChange']();
    }
  }

  public override getValue(keepHidden: boolean = true): T[] {
    if (!this.withAutoEntityStatus) {
      return this.elements.map(e =>
        this.questionControlService.toValues(e, keepHidden),
      );
    }

    const deletedElements = this._deletedItems.map(e => [
      ...e,
      this._initEntityStatusQuestion(EntityStatus.DELETE),
    ]);

    const newOrEditOrIsoElements = this.elements.map(elements => {
      const idElement = getElement<QuestionHidden<number>>('id', elements);

      if (!idElement.getValue()) {
        return [...elements, this._initEntityStatusQuestion(EntityStatus.NEW)];
      }

      const isPristine = this._getQuestions(elements).every(e => e.pristine);

      return [
        ...elements,
        this._initEntityStatusQuestion(
          isPristine ? EntityStatus.ISO : EntityStatus.EDIT,
        ),
      ];
    });

    return [...deletedElements, ...newOrEditOrIsoElements].map(e =>
      this.questionControlService.toValues(e, keepHidden),
    );
  }

  public getLabel(index: number): string {
    return this.getTitle(index)?.getValue() ?? null;
  }

  public getMarkTooltip(index: number): string {
    return this.getTitle(index)?.questionMarkTooltip ?? null;
  }

  public getTitle(index: number): QuestionTitle {
    if (!this.elements[index]) {
      return null;
    }

    return this.elements[index].find(q => q instanceof QuestionTitle);
  }

  public getCollapseKpi(index: number): QuestionCollapseKpi {
    if (!this.elements[index]) {
      return null;
    }

    return this.elements[index].find(q => q instanceof QuestionCollapseKpi);
  }

  public getQuestionCollapseIconValue(index: number): string | null {
    const icon = this._getQuestionCollapseIcon(index);

    return icon?.getValue() ?? null;
  }

  public getQuestionCollapseIconTooltip(index: number): string | null {
    const icon = this._getQuestionCollapseIcon(index);

    return icon?.iconTooltip ?? null;
  }

  public getQuestionCollapseIconColor(index: number): string {
    const icon = this._getQuestionCollapseIcon(index);

    return icon?.color ?? null;
  }

  public get controls(): FormGroup[] {
    return (this._formControl ? this._formControl.controls : []) as FormGroup[];
  }

  /**
   * @deprecated
   * Use enable or disable instead
   */
  public override setEnabled(
    enabled: boolean,
    keepAtPosition: boolean = false,
  ): void {
    if (enabled) {
      this.enable();
    } else {
      this.disable(keepAtPosition);
    }
  }

  /**
   * @deprecated
   * Use enable or disable instead
   */
  public override setDisabled(
    disabled: boolean,
    keepAtPosition: boolean = false,
  ): void {
    this.setEnabled(!disabled, keepAtPosition);
  }

  public override enable(): void {
    const children = this.controls;
    this._formControl.controls = [];

    super.enable();

    this._formControl.controls = children;

    this.update();
  }

  public override disable(keepAtPosition: boolean = false): void {
    const children = this.controls;
    this._formControl.controls = [];

    super.disable(keepAtPosition);

    this._formControl.controls = children;
  }

  public override getFormControl(): UntypedFormArray {
    return this._formControl;
  }

  public async removeItemRequest(
    index: number,
    questionGroup?: QuestionGroup<T>,
  ): Promise<boolean> {
    const canDelete = await (this.canDelete
      ? Promise.resolve(this.canDelete())
      : Promise.resolve(true));

    if (
      canDelete &&
      (!this.canDeleteItem || this.canDeleteItem(questionGroup, index))
    ) {
      this.removeItem(index);
    }

    return canDelete;
  }

  public override equals(values: T[]): boolean {
    if (!Array.isArray(values)) {
      return false;
    }

    if (values.length !== this.elements.length) {
      return false;
    }

    for (let i = 0; i < values.length; i++) {
      if (!this._equals(values[i], this.elements[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Display the standard add label button if:
   * - we have the value to initialise the new element with
   * - we don't have a custom action
   * - we have no item line and need to add the first ( custom action button is bound to the item line)
   */
  public addButtonDisplayConditions(): boolean {
    const hasInitialValue =
      this.initialItemValue || this.initialItemValueFactory instanceof Function;

    return (
      (hasInitialValue && !this.customActionAddItem) ||
      (this.elements.length === 0 && this.customActionAddItem)
    );
  }

  public isAddButtonDisabled(): boolean {
    return !!this.maxItems && this.elements.length >= this.maxItems;
  }

  /**
   * @internal
   */
  public callCustomActionCallback(index: number): void {
    if (!this.customActionCallback) {
      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.warn('No custom action callback defined');
      }

      return;
    }

    const values = this.getValue();

    if (!values || values.length < index) {
      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.warn('Current index is out of bounds');
      }

      return;
    }

    this.customActionCallback(values[index], index);
  }

  private _equals(values: T, elements: ElementBase[]): boolean {
    const questions = this._getQuestions(elements);

    return !questions.some(q => {
      const obj = values;
      // @ts-expect-error
      let compareValue = obj[q.key];

      if (q.elementType === 'alternativeInput') {
        const altInput = q as unknown as QuestionAlternativeInput;
        const { key, secondaryOutputKey } = altInput;

        compareValue = {
          // @ts-expect-error
          [key]: obj[key],
          // @ts-expect-error
          [secondaryOutputKey]: obj[secondaryOutputKey],
        };
      }

      return !q.equals(compareValue);
    });
  }

  private _getQuestions(elements: ElementBase[]): QuestionBase<any>[] {
    return elements.filter(
      element =>
        element instanceof QuestionBase &&
        !(element instanceof QuestionHidden) &&
        !(element instanceof QuestionCollapseKpi) &&
        !(element instanceof QuestionCollapseIcon) &&
        !(element instanceof QuestionTitle) &&
        !element.excludeFromControls &&
        !(element instanceof QuestionFinancialValue && element.displayOnly) &&
        !element.disabled,
    ) as QuestionBase<any>[];
  }

  private _getQuestionCollapseIcon(index: number): QuestionCollapseIcon {
    if (!this.elements[index]) {
      return null;
    }

    return (
      this.elements[index].find(q => q instanceof QuestionCollapseIcon) || null
    );
  }

  private _setCachedCollapseStatus(): void {
    this.questionControlService.setCachedCollapseStatus(
      this.key,
      this._collapsableToggleValues,
    );
  }

  private _initEntityStatusQuestion(
    entityStatus: EntityStatus,
  ): QuestionHidden<EntityStatus> {
    const question = new QuestionHidden<EntityStatus>({
      key: 'entityStatus',
      initialValue: entityStatus,
    });

    const control = new QuestionControl();

    question.registerFormControl(control);
    question.setValue(entityStatus);

    return question;
  }
}
